"use client";

import { createContext, use, useState } from "react";
import { useDeliveryLocation } from "@/hooks/checkout/use-delivery-location";
import {
  defaultDeliveryWindow,
  type DeliveryWindow,
} from "@/lib/checkout/delivery-window";
import { getListingQuantityStep } from "@shared/products/checkout";
import {
  getInitialListingQuantity,
  getListingQuantityPicks,
  normalizeListingQuantity,
  type ListingQuantityPick,
} from "./listing-action-state";
import { useListingQuery } from "./listing-query";
import type { SiteAccess } from "@convex/schemas/validators/siteAccess.lib";

interface ListingBuyState {
  quantity: number;
  setQuantity: (quantity: number) => void;
  quantityPending: boolean;
  setQuantityPending: (pending: boolean) => void;
  minQuantity: number;
  checkoutMinimum: number;
  quantityStep: number;
  quantityPicks: ListingQuantityPick[];
  deliveryWindow: DeliveryWindow;
  setDeliveryWindow: (window: DeliveryWindow) => void;
  siteAccess: SiteAccess;
  setSiteAccess: (access: SiteAccess) => void;
  leadTimeDays: number;
  delivery: ReturnType<typeof useDeliveryLocation>;
  addressDialog: ListingAddressDialogIntent | null;
  openAddressDialog: (intent: ListingAddressDialogIntent) => void;
  closeAddressDialog: () => void;
}

export type ListingAddressDialogIntent = "edit" | "buy";

export const LISTING_DELIVERY_ADDRESS_INPUT_ID = "listing-delivery-address";

const ListingBuyStateContext = createContext<ListingBuyState | null>(null);
const MIN_LISTING_QUANTITY = 1;

interface ListingBuyStateProviderProps {
  checkoutMinimum: number;
  truckCapacity: number;
  suggestedQuantity: number;
  leadTimeDays: number;
  children: React.ReactNode;
}

export function ListingBuyStateProvider({
  checkoutMinimum,
  truckCapacity,
  suggestedQuantity,
  leadTimeDays,
  children,
}: ListingBuyStateProviderProps) {
  const minQuantity = MIN_LISTING_QUANTITY;
  const [quantity, setQuantity] = useRequestedQuantity(suggestedQuantity);
  const [deliveryWindow, setDeliveryWindow] = useState(() =>
    defaultDeliveryWindow(leadTimeDays),
  );
  const [quantityPending, setQuantityPending] = useState(false);
  const [addressDialog, setAddressDialog] =
    useState<ListingAddressDialogIntent | null>(null);
  const delivery = useDeliveryLocation();
  const [siteAccess, setSiteAccess] = useSiteAccessFollowingAddress(delivery);

  const value: ListingBuyState = {
    quantity,
    setQuantity: (next: number) => setQuantity(normalizeListingQuantity(next)),
    quantityPending,
    setQuantityPending,
    minQuantity,
    checkoutMinimum,
    quantityStep: getListingQuantityStep(checkoutMinimum, truckCapacity),
    quantityPicks: getListingQuantityPicks({ checkoutMinimum, truckCapacity }),
    deliveryWindow,
    setDeliveryWindow,
    siteAccess,
    setSiteAccess,
    leadTimeDays,
    delivery,
    addressDialog,
    openAddressDialog: setAddressDialog,
    closeAddressDialog: () => setAddressDialog(null),
  };

  return (
    <ListingBuyStateContext value={value}>
      {children}
    </ListingBuyStateContext>
  );
}

export function useListingBuyState(): ListingBuyState {
  const state = use(ListingBuyStateContext);
  if (!state) {
    throw new Error(
      "useListingBuyState must be used inside a ListingBuyStateProvider",
    );
  }
  return state;
}

function useRequestedQuantity(suggestedQuantity: number) {
  const searchParams = useListingQuery();
  return useState(() =>
    getInitialListingQuantity(
      searchParams.get("qty") ?? undefined,
      suggestedQuantity,
    ),
  );
}

const DEFAULT_SITE_ACCESS: SiteAccess = { dock: true };

function useSiteAccessFollowingAddress(
  delivery: ReturnType<typeof useDeliveryLocation>,
) {
  const savedAddressId = delivery.location?.savedAddressId;
  const [siteAccess, setSiteAccess] = useState(DEFAULT_SITE_ACCESS);
  const [followedId, setFollowedId] = useState<string | undefined>(undefined);
  if (savedAddressId !== followedId) {
    const saved = delivery.savedAddressOptions.find(
      (option) => option.id === savedAddressId,
    );
    if (!savedAddressId || saved) {
      setFollowedId(savedAddressId);
      if (saved?.siteAccess) setSiteAccess(saved.siteAccess);
      else if (!savedAddressId) setSiteAccess(DEFAULT_SITE_ACCESS);
    }
  }
  return [siteAccess, setSiteAccess] as const;
}
